import { useCallback, useEffect, useRef, useState } from "react";

interface UseResizeHandlingOptions {
  minWidth: number;
  maxWidth: number;
  defaultWidth?: number;
  resizable: boolean;
  onWidthChange?: (width: number) => void;
  isOpen: boolean;
}

interface UseResizeHandlingReturn {
  sidebarWidth: number;
  isCustomWidth: boolean;
  isResizing: boolean;
  startResizing: (e: React.MouseEvent) => void;
  resizeRef: React.RefObject<HTMLDivElement>;
}

const useResizeHandling = ({
  minWidth,
  maxWidth,
  defaultWidth,
  resizable,
  onWidthChange,
  isOpen,
}: UseResizeHandlingOptions): UseResizeHandlingReturn => {
  const [sidebarWidth, setSidebarWidth] = useState(defaultWidth || minWidth);
  const [isCustomWidth, setIsCustomWidth] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<HTMLDivElement>(null);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();

      const diffX = startX.current - e.pageX;
      const newWidth = Math.min(Math.max(startWidth.current + diffX, minWidth), maxWidth);
      setSidebarWidth(newWidth);
      setIsCustomWidth(true);
      onWidthChange?.(newWidth);
    },
    [minWidth, maxWidth, onWidthChange],
  );

  const stopResizing = useCallback(() => {
    setIsResizing(false);
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", stopResizing);
    document.body.style.cursor = "";
  }, [handleMouseMove]);

  const startResizing = useCallback(
    (e: React.MouseEvent) => {
      if (!resizable) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      setIsResizing(true);
      startX.current = e.pageX;
      startWidth.current = sidebarWidth;
      document.body.style.cursor = "ew-resize";

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", stopResizing);
    },
    [handleMouseMove, sidebarWidth, stopResizing, resizable],
  );

  // Cleanup event listeners on unmount
  useEffect(
    () => () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", stopResizing);
      document.body.style.cursor = "";
    },
    [handleMouseMove, stopResizing],
  );

  // Reset width when sidebar opens/closes
  useEffect(() => {
    if (!isCustomWidth) {
      setSidebarWidth(defaultWidth || minWidth);
    }
  }, [isOpen, defaultWidth, minWidth, isCustomWidth]);

  // Reset custom width flag when sidebar closes
  useEffect(() => {
    if (!isOpen) {
      setIsCustomWidth(false);
    }
  }, [isOpen]);

  return {
    sidebarWidth,
    isCustomWidth,
    isResizing,
    startResizing,
    resizeRef,
  };
};

export default useResizeHandling;
